import { forwardRef } from 'react';
import { ArrowRight, CalendarPlus, MapPin } from 'lucide-react';
import TemplateFrame from './TemplateFrame';
import ClubBrandMark from './ClubBrandMark';
import GuestStyleGridTable from './GuestStyleGridTable';
import PickleballBall from './PickleballBall';
import PickleballPaddle from './PickleballPaddle';
import { AVAILABILITY_IMAGE_LANDSCAPE } from '../../constants/bookingConst';
import { fitClubNameSize } from '../../utils/availabilitySnapshotUtil';

// Leaf green for the drawn icons (they take colour as a prop, not from CSS).
// Timeslot table sizing (see planGridRows): hourly rows down to 0.8× the
// guest page's size, merged ranges below that.
const GRID_SIZING = { bodyHeight: 582, minHourlyUnit: 0.8, maxUnit: 1.5, maxRowHeight: 80, tableWidth: 1376, maxHeaderUnit: 1.3 };

/**
 * Template — "Court Availability", light design: every hour × every court
 * on a white card over an off-white page, with an open-slot /
 * Book Online footer.
 * @param {object} props
 * @param {object} props.snapshot - buildAvailabilitySnapshot() output
 * @param {'ranges'|'hourly'} [props.rowMode] - 'hourly' forces one row per hour; 'ranges' merges hours when hourly rows would be too small
 * @param {object} [props.imageSettings] - Availability images settings (font sizes in px, BOOKED colours); see availabilityImageSettingsUtil
 * @returns {JSX.Element}
 */
const FullGridLightTemplate = forwardRef(function FullGridLightTemplate({ snapshot, rowMode, imageSettings }, ref) {
  // The club's Active Button Color, deepened to read on the light background.
  const leaf = snapshot.palette.accentOnLight;
  const note = snapshot.hasPromo ? 'Promo rates on select hours' : snapshot.hasRateRange ? 'Peak hours apply' : '';

  return (
    <TemplateFrame
      ref={ref}
      width={AVAILABILITY_IMAGE_LANDSCAPE.width}
      height={AVAILABILITY_IMAGE_LANDSCAPE.height}
      className="avg-light"
      palette={snapshot.palette}
    >
      {/* ---- decoration ---- */}
      <div className="avg-l-panel-shape" />
      <PickleballPaddle width={180} className="avg-l-paddle" />
      <PickleballBall size={92} className="avg-l-ball" />

      {/* ---- header ---- */}
      <ClubBrandMark
        snapshot={snapshot}
        iconSize={62}
        iconColor={leaf}
        nameSize={fitClubNameSize(snapshot.clubNameMain, 37, 20)}
        className="avg-l-mark"
      />
      <div className="avg-l-date">
        <span className="avg-l-date-day">{snapshot.weekdayUpper}</span>
        <span className="avg-l-date-full">{snapshot.dateCompactUpper}</span>
      </div>
      <div className="avg-l-vrule avg-l-vrule-head" />
      <div className="avg-l-motto">
        <span>Great games</span>
        <span>start with</span>
        <span>good courts.</span>
      </div>

      <div className="avg-l-title">Court <em>Availability</em></div>
      <div className="avg-l-subtitle">Reserve your court. Play your game.</div>

      {/* ---- table ---- */}
      <div className="avg-l-card">
        <GuestStyleGridTable
          snapshot={snapshot}
          bodyHeight={GRID_SIZING.bodyHeight}
          minHourlyUnit={GRID_SIZING.minHourlyUnit}
          maxUnit={GRID_SIZING.maxUnit}
          maxRowHeight={GRID_SIZING.maxRowHeight}
          tableWidth={GRID_SIZING.tableWidth}
          maxHeaderUnit={GRID_SIZING.maxHeaderUnit}
          rowMode={rowMode}
          imageSettings={imageSettings}
        />
      </div>

      {/* ---- footer ---- */}
      <div className="avg-l-footer">
        <CalendarPlus size={40} color={leaf} strokeWidth={1.8} />
        <div className="avg-l-slots">
          <span className="avg-l-slots-label">Open slots</span>
          <span className="avg-l-slots-value">
            {snapshot.openCount}<small> of </small>{snapshot.totalCount}
          </span>
        </div>
        {note && (
          <>
            <div className="avg-l-vrule" />
            <span className="avg-l-note">{note}</span>
          </>
        )}
        <div className="avg-l-footer-right">
          {snapshot.siteHost && (
            <>
              <div className="avg-l-vrule avg-l-vrule-tall" />
              <span className="avg-l-host"><MapPin size={28} color={leaf} strokeWidth={1.8} />{snapshot.siteHost}</span>
            </>
          )}
          <span className="avg-l-cta">Book online <ArrowRight size={22} strokeWidth={2} /></span>
        </div>
      </div>
    </TemplateFrame>
  );
});

export default FullGridLightTemplate;
